import { getSessionFileOutput } from "@/app/providers/services";
import { JsonEditor } from "@/common/JsonEditor";
import { CSVReadOnlyPreview } from "@/pages/models/release/Technical/MappingV2/CSVPreview";
import { JSONDataEditor } from "@/pages/models/release/Technical/MappingV2/JSONDataEditor";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import Tabs from "@imminently/decisively-ui/Tabs";
import { Typography } from "@mui/material";
import DOMPurify from "dompurify";
import React from "react";
import { useQuery } from "react-query";

const decodeBase64Utf8 = (value: string): string => {
  if (!value) return "";
  try {
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
};

const detectFileType = (name: string, contentType: string | undefined, text: string): "csv" | "json" | "unknown" => {
  const normalisedName = String(name || "").toLowerCase();
  const normalisedType = String(contentType || "").toLowerCase();
  if (normalisedType.includes("csv") || normalisedName.endsWith(".csv")) return "csv";
  if (normalisedType.includes("json") || normalisedName.endsWith(".json")) return "json";
  try {
    JSON.parse(text);
    return "json";
  } catch {
    return "unknown";
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toDisplayString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value) || "";
  } catch {
    return String(value);
  }
};

const toDisplayList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(toDisplayString).filter(Boolean);
  const stringValue = toDisplayString(value);
  return stringValue ? [stringValue] : [];
};

const getNestedValue = (source: unknown, path: string[]): unknown => {
  return path.reduce<unknown>((current, key) => (isRecord(current) ? current[key] : undefined), source);
};

const firstPresent = (...values: unknown[]): unknown => values.find((value) => value !== undefined && value !== null && value !== "");

const getCommsMethod = (interaction: unknown): string => {
  const method = firstPresent(
    getNestedValue(interaction, ["outcome", "method"]),
    getNestedValue(interaction, ["data", "method"]),
    getNestedValue(interaction, ["request", "method"]),
  );
  return toDisplayString(method).toLowerCase();
};

const isCommsInteraction = (interaction: unknown): boolean => {
  return getNestedValue(interaction, ["mode"]) === "comms" || getNestedValue(interaction, ["data", "type"]) === "comms";
};

const CommsField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-border last:border-b-0">
    <Typography variant="caption" color="textSecondary" className="col-span-3">
      {label}
    </Typography>
    <div className="col-span-9 min-w-0">
      {children}
    </div>
  </div>
);

const CommsRecipientList = ({ values }: { values: string[] }) => {
  if (values.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No recipients recorded
      </Typography>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value, index) => (
        <span
          key={`${value}-${index}`}
          className="rounded bg-muted px-2 py-1 text-sm text-foreground break-all"
        >
          {value}
        </span>
      ))}
    </div>
  );
};

const CommsDataView = ({ interaction }: { interaction: unknown }) => {
  const method = getCommsMethod(interaction);
  const isEmail = method === "email";
  const isSms = method === "sms";
  const recipientLabel = isSms ? "SMS to" : isEmail ? "Email to" : "Sent to";
  const contentLabel = isSms ? "Message" : isEmail ? "Email" : "Message";
  const recipients = toDisplayList(firstPresent(
    getNestedValue(interaction, ["outcome", "recipients"]),
    getNestedValue(interaction, ["request", "to"]),
    getNestedValue(interaction, ["request", "body", "to"]),
    getNestedValue(interaction, ["data", "who"]),
  ));
  const ccRecipients = isEmail
    ? toDisplayList(firstPresent(getNestedValue(interaction, ["request", "cc"]), getNestedValue(interaction, ["request", "body", "cc"])))
    : [];
  const subject = isEmail
    ? toDisplayString(firstPresent(getNestedValue(interaction, ["outcome", "subject"]), getNestedValue(interaction, ["data", "subject"])))
    : "";
  const message = toDisplayString(firstPresent(
    isEmail ? getNestedValue(interaction, ["request", "htmlContent"]) : undefined,
    isEmail ? getNestedValue(interaction, ["data", "htmlContent"]) : undefined,
    getNestedValue(interaction, ["request", "bodyContent"]),
    getNestedValue(interaction, ["request", "body", "body"]),
    getNestedValue(interaction, ["data", "body"]),
  ));
  const sanitizedEmailHtml = React.useMemo(
    () => (isEmail ? DOMPurify.sanitize(message, { USE_PROFILES: { html: true } }) : ""),
    [isEmail, message],
  );

  return (
    <div className="h-full">
      <div className="max-w-5xl">
        <CommsField label={recipientLabel}>
          <CommsRecipientList values={recipients} />
        </CommsField>
        {ccRecipients.length > 0 && (
          <CommsField label="CC">
            <CommsRecipientList values={ccRecipients} />
          </CommsField>
        )}
        {isEmail && subject && (
          <CommsField label="Subject">
            <Typography variant="body2" style={{ fontWeight: 500, wordBreak: "break-word" }}>
              {subject}
            </Typography>
          </CommsField>
        )}
        <CommsField label={contentLabel}>
          {message ? (
            isEmail ? (
              <div className="space-y-3">
                <div
                  role="note"
                  className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <span className="font-medium">Preview only:</span> This email is shown without template styling. Styling is
                  applied from the template selected at runtime, so this preview may differ from what the recipient receives.
                </div>
                <iframe
                  title="Email content"
                  sandbox=""
                  referrerPolicy="no-referrer"
                  srcDoc={sanitizedEmailHtml}
                  className="min-h-96 w-full rounded border border-border bg-white"
                />
              </div>
            ) : (
              <div className="rounded border border-border bg-muted/30 p-4 text-sm whitespace-pre-wrap">
                {message}
              </div>
            )
          ) : (
            <Typography variant="body2" color="textSecondary">
              No message recorded
            </Typography>
          )}
        </CommsField>
      </div>
    </div>
  );
};

const OutboundInteractionDetail = ({ interaction, sessionId }: { interaction: any; sessionId?: string }) => {
  const isComms = isCommsInteraction(interaction);
  const hasRequest = interaction?.request != null;
  const hasResponse = interaction?.response != null;
  const defaultTab = isComms ? "data" : hasRequest ? "request" : hasResponse ? "response" : "outcome";
  const [activeTab, setActiveTab] = React.useState<string>(defaultTab);
  const hasError = interaction?.status === "error";
  const outcome = interaction?.outcome;
  const hasDiagnostics = interaction?.integrationMeta != null;
  const attemptCount = Number.isFinite(Number(interaction?.attempts)) ? Number(interaction.attempts) : null;
  const retryCount = Number.isFinite(Number(interaction?.retryCount)) ? Number(interaction.retryCount) : null;
  const fallbackFileName = interaction?.outcome?.title || interaction?.data?.title;
  const hasFile = interaction?.mode === "storage" && Boolean(sessionId) && Boolean(interaction?.id);

  React.useEffect(() => {
    setActiveTab(defaultTab);
  }, [interaction?.id, defaultTab]);

  React.useEffect(() => {
    if (!hasFile && activeTab === "file") {
      setActiveTab(defaultTab);
    }
  }, [hasFile, activeTab, defaultTab]);

  const {
    data: filePayload,
    isLoading: isLoadingFile,
    error: fileError,
  } = useQuery(
    ["session-storage-file", sessionId, interaction?.id],
    () => getSessionFileOutput(sessionId as string, String(interaction?.id)),
    {
      enabled: hasFile && activeTab === "file",
      staleTime: 60_000,
    },
  );

  const fileText = React.useMemo(() => decodeBase64Utf8(String(filePayload?.data || "")), [filePayload?.data]);
  const fileType = React.useMemo(
    () => detectFileType(String(filePayload?.name || fallbackFileName || ""), filePayload?.contentType, fileText),
    [filePayload?.contentType, filePayload?.name, fallbackFileName, fileText],
  );
  const parsedFileJson = React.useMemo(() => {
    if (fileType !== "json") return null;
    try {
      return JSON.parse(fileText);
    } catch {
      return null;
    }
  }, [fileType, fileText]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Typography variant="h6" style={{ marginBottom: 8, paddingLeft: 16, paddingTop: 16 }}>
        Outbound: {interaction?.mode ?? "—"}
      </Typography>
      <div className="grid grid-cols-12 gap-2 px-4">
        <div className="col-span-3">
          <Typography variant="caption" color="textSecondary">
            Status
          </Typography>
        </div>
        <div className="col-span-9">
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {interaction?.status ?? "—"}
          </Typography>
        </div>
        {hasError && interaction?.errorMessage && (
          <>
            <div className="col-span-3">
              <Typography variant="caption" color="textSecondary">
                Error
              </Typography>
            </div>
            <div className="col-span-9">
              <Typography variant="body2" color="error" style={{ wordBreak: "break-word" }}>
                {interaction.errorMessage}
              </Typography>
            </div>
          </>
        )}
        {(attemptCount !== null || retryCount !== null) && (
          <>
            <div className="col-span-3">
              <Typography variant="caption" color="textSecondary">
                Retries
              </Typography>
            </div>
            <div className="col-span-9">
              <Typography variant="body2">
                {retryCount ?? 0} retries over {attemptCount ?? 1} attempt{attemptCount === 1 ? "" : "s"}
              </Typography>
            </div>
          </>
        )}
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
        <Tabs.List className="px-4 pt-4 border-b border-border">
          <Tabs.Trigger value="data">Data</Tabs.Trigger>
          {isComms && <Tabs.Trigger value="payload">Payload</Tabs.Trigger>}
          {hasRequest && <Tabs.Trigger value="request">Request</Tabs.Trigger>}
          {hasResponse && <Tabs.Trigger value="response">Response</Tabs.Trigger>}
          {!isComms && <Tabs.Trigger value="outcome">Outcome</Tabs.Trigger>}
          {hasFile && <Tabs.Trigger value="file">File</Tabs.Trigger>}
          {hasDiagnostics && <Tabs.Trigger value="diagnostics">Diagnostics</Tabs.Trigger>}
        </Tabs.List>
        <Tabs.Content value="data" className="flex-1 overflow-auto p-4">
          {isComms ? (
            <CommsDataView interaction={interaction} />
          ) : (
            <JsonEditor
              value={JSON.stringify(interaction?.data ?? {}, null, 2)}
              disabled
              withCopy
            />
          )}
        </Tabs.Content>
        {isComms && (
          <Tabs.Content value="payload" className="flex-1 overflow-auto p-4">
            <JsonEditor
              value={JSON.stringify(interaction?.data ?? {}, null, 2)}
              disabled
              withCopy
            />
          </Tabs.Content>
        )}
        {hasRequest && (
          <Tabs.Content value="request" className="flex-1 overflow-auto p-4">
            <JsonEditor
              value={
                typeof interaction.request === "object"
                  ? JSON.stringify(interaction.request, null, 2)
                  : String(interaction.request ?? "")
              }
              disabled
              withCopy
            />
          </Tabs.Content>
        )}
        {hasResponse && (
          <Tabs.Content value="response" className="flex-1 overflow-auto p-4">
            <JsonEditor
              value={
                typeof interaction.response === "object"
                  ? JSON.stringify(interaction.response, null, 2)
                  : String(interaction.response ?? "")
              }
              disabled
              withCopy
            />
          </Tabs.Content>
        )}
        {!isComms && (
          <Tabs.Content value="outcome" className="flex-1 overflow-auto p-4">
            {outcome !== undefined && outcome !== null ? (
              <JsonEditor
                value={
                  typeof outcome === "object" ? JSON.stringify(outcome, null, 2) : String(outcome)
                }
                disabled
                withCopy
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No outcome yet
              </Typography>
            )}
          </Tabs.Content>
        )}
        {hasFile && (
          <Tabs.Content value="file" className="flex-1 overflow-auto p-4">
            {isLoadingFile ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm"  />
                <Typography variant="body2" color="textSecondary">
                  Loading file...
                </Typography>
              </div>
            ) : fileError ? (
              <Typography variant="body2" color="error">
                {(fileError as Error)?.message || "Failed to load file"}
              </Typography>
            ) : fileType === "csv" ? (
              <CSVReadOnlyPreview csvData={fileText} />
            ) : fileType === "json" && parsedFileJson != null ? (
              <JSONDataEditor value={parsedFileJson} title="File Output" readOnly />
            ) : fileText ? (
              <JsonEditor value={fileText} disabled withCopy />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No file content available
              </Typography>
            )}
          </Tabs.Content>
        )}
        {hasDiagnostics && (
          <Tabs.Content value="diagnostics" className="flex-1 overflow-auto p-4">
            <JsonEditor
              value={
                typeof interaction.integrationMeta === "object"
                  ? JSON.stringify(interaction.integrationMeta, null, 2)
                  : String(interaction.integrationMeta ?? "")
              }
              disabled
              withCopy
            />
          </Tabs.Content>
        )}
      </Tabs>
    </div>
  );
};

export default OutboundInteractionDetail;
